import { createApp, markRaw } from 'vue';
import App from './App.vue';
import router from './router';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import http, { fetchCSRFToken } from './services/http';
import { 
  // Bootstrap-Vue-Next Komponenten
  BModal,
  BButton,
  BAlert,
  BCard,
  BForm,
  BFormInput,
  BFormGroup,
  BInputGroup,
  BModalPlugin,
  BNavbar,
  BContainer,
  BRow,
  BCol,
  createBootstrap
} from 'bootstrap-vue-next';
import Toast, { useToast } from 'vue-toastification';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css';
import 'vue-toastification/dist/index.css';
import 'animate.css';
// Font Awesome importieren
import '@fortawesome/fontawesome-free/css/all.min.css';
import './assets/scss/main.scss';
// Benötigte Stores importieren
import { useAuthStore } from './stores/auth';
import { useCompanyStore } from './stores/company';
import { useTestsStore } from './stores/tests';
import { useApplicantsStore } from './stores/applicants';

document.title = process.env.VUE_APP_NAME;

// Pinia Store einrichten
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

// Router im Store verfügbar machen
pinia.use(({ store }) => {
  store.router = markRaw(router);
});

// App erstellen
const app = createApp(App);

// HTTP-Service global verfügbar machen
app.config.globalProperties.$http = http;
app.config.globalProperties.$toast = useToast();

// Feature flags für Vue
// Löst die Warnung: "__VUE_PROD_HYDRATION_MISMATCH_DETAILS__ is not explicitly defined"
app.config.performance = false;
app.config.compilerOptions = {
  isCustomElement: (tag) => tag.startsWith('b-')
};

// Theme-Handling für Unternehmensfarben
app.mixin({
  methods: {
    applyCompanyTheme(primaryColor) {
      document.documentElement.style.setProperty('--primary-color', primaryColor || '#3490dc');
    }
  }
});

// Globales Flag für die Firmeninitialisierung
app.config.globalProperties.$companyLoadInProgress = false;

// Hilfsfunktion für das Laden von Firmeninformationen
app.config.globalProperties.$loadCompanyForUser = async function() {
  // Wenn bereits ein Ladevorgang läuft, nicht erneut starten
  if (this.$companyLoadInProgress) {
    console.log('Firmenladung bereits im Gange, überspringe doppelten Aufruf');
    return null;
  }
  
  // Flag setzen, dass der Ladevorgang begonnen hat
  this.$companyLoadInProgress = true;
  
  try {
    const authStore = useAuthStore();
    const companyStore = useCompanyStore();
    const user = authStore.user;
    
    if (!user) {
      console.warn('Keine Benutzerinformationen verfügbar');
      this.$companyLoadInProgress = false;
      return null;
    }
    
    // Prüfen, ob bereits Firmendaten geladen sind
    const currentCompany = companyStore.currentCompany;
    if (currentCompany) {
      console.log('Firma bereits geladen:', currentCompany.name);
      this.$companyLoadInProgress = false;
      return currentCompany;
    }
    
    // Aktuelle Firmen-Daten zurücksetzen
    localStorage.removeItem('currentCompany');
    localStorage.removeItem('companyId');
    
    // Firmeninformationen laden, wenn der Benutzer einer Firma zugeordnet ist
    if (user.company_id) {
      console.log('Lade Firma für den aktuellen Benutzer:', user.name, 'Firma:', user.company_id);
      
      // Für normale Firmenbenutzer direkt die eigene Firma laden (ohne Admin-API)
      if (user.role !== 'super_admin') {
        const company = await companyStore.fetchOwnCompany();
        this.$companyLoadInProgress = false;
        return company;
      }
      // Nur für Super-Admins die normale Methode verwenden
      const company = await companyStore.setCurrentCompanyById(user.company_id);
      this.$companyLoadInProgress = false;
      return company;
    } else if (user.role === 'super_admin') {
      // Für Superadmins: Liste aller Firmen laden (für spätere Auswahl)
      await companyStore.fetchCompanies();
    }
  } catch (error) {
    console.error('Fehler beim Laden der Firma:', error);
  } finally {
    // Immer das Flag zurücksetzen, auch bei Fehlern
    this.$companyLoadInProgress = false;
  }
  
  return null;
};

// Bootstrap-Vue Modal-Management verbessern
window.$bvModalGlobal = {
  show(modalId) {
    console.log(`Versuche Modal ${modalId} anzuzeigen...`);
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      console.log(`Modal ${modalId} gefunden, zeige an...`);
      const modalInstance = app.config.globalProperties.$bvModal;
      if (modalInstance) {
        modalInstance.show(modalId);
      } else {
        console.error(`$bvModal nicht gefunden beim Anzeigen von ${modalId}`);
      }
    } else {
      console.warn(`Modal mit ID ${modalId} konnte nicht gefunden werden.`);
    }
  },
  hide(modalId) {
    console.log(`Versuche Modal ${modalId} zu verstecken...`);
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      console.log(`Modal ${modalId} gefunden, verstecke...`);
      const modalInstance = app.config.globalProperties.$bvModal;
      if (modalInstance) {
        modalInstance.hide(modalId);
      } else {
        console.error(`$bvModal nicht gefunden beim Verstecken von ${modalId}`);
      }
    } else {
      console.warn(`Modal mit ID ${modalId} konnte nicht gefunden werden.`);
    }
  }
};

// VUE_PROD_HYDRATION_MISMATCH_DETAILS für Debugging aktivieren
window.__VUE_PROD_HYDRATION_MISMATCH_DETAILS__ = true;

// Füge ein globales $bvModal Objekt hinzu als Polyfill für Bootstrap-Vue
app.config.globalProperties.$bvModal = window.$bvModalGlobal;

// Configure Bootstrap-Vue-Next
const { BootstrapVue } = createBootstrap({
  components: {
    BButton,
    BForm,
    BFormGroup,
    BInputGroup,
    BModal,
    BFormInput,
    BCard,
    BNavbar,
    BContainer,
    BRow,
    BCol
  }
});

// Install Bootstrap-Vue-Next
app.use(BootstrapVue);
app.use(BModalPlugin);

// Anwendungsinitialisierung in einer strukturierten async-Funktion
async function initializeApp() {
  try {
    // 1. Pinia einbinden
    app.use(pinia);
    
    // 2. Bootstrap-Vue-Next einbinden
    app.use(BootstrapVue);
    app.use(BModalPlugin);
    
    // 3. Registriere die Bootstrap-Komponenten einzeln und explizit
    app.component('BModal', BModal);
    app.component('BButton', BButton);
    app.component('BAlert', BAlert);
    app.component('BCard', BCard);
    app.component('BForm', BForm);
    app.component('BFormInput', BFormInput);
    app.component('BFormGroup', BFormGroup);
    app.component('BInputGroup', BInputGroup);
    
    // 4. Toast-Benachrichtigungen installieren
    app.use(Toast, {
      transition: "Vue-Toastification__bounce",
      maxToasts: 3,
      newestOnTop: true
    });

    // 5. Router einbinden
    app.use(router);
    
    // 6. Auth und Company initialisieren
    console.log('Initialisiere Authentifizierung...');
    const authStore = useAuthStore();
    await authStore.initializeAuth();
    
    // Log Auth Status nach Initialisierung
    console.log('Auth Status nach Initialisierung - isAuthenticated:', authStore.isAuthenticated, 'Token:', !!authStore.token, 'User:', !!authStore.user);
    
    // 7. CSRF-Token nur abrufen, wenn der Benutzer authentifiziert ist
    if (authStore.isAuthenticated) {
      console.log('Hole CSRF-Token für authentifizierten Benutzer...');
      await fetchCSRFToken().catch(error => {
        console.error('Fehler beim Abrufen des CSRF-Tokens:', error);
      });
    }
    
    // 8. Firmendaten laden, wenn der Benutzer authentifiziert ist
    if (authStore.isAuthenticated) {
      console.log('Initialisiere Firmendaten...');
      const companyStore = useCompanyStore();
      await companyStore.initializeCompany().catch(error => {
        console.error('Fehler bei der Firmeninitialisierung:', error);
      });
    }
    
    console.log('Anwendungsinitialisierung abgeschlossen.');
    
    // 9. App starten
    app.mount('#app');
  } catch (error) {
    console.error('Fehler bei der Anwendungsinitialisierung:', error);
  }
}

// Anwendung starten
initializeApp(); 